export type ReportRichTextMark = {
  type: "bold" | "italic" | "underline";
};

export type ReportFontSize = "small" | "normal" | "large";

export type ReportRichTextInline = {
  type: "text" | "hardBreak";
  text?: string;
  marks?: ReportRichTextMark[];
};

export type ReportRichTextParagraph = {
  type: "paragraph";
  attrs?: {
    textAlign?: "left" | "center" | "right" | "justify" | null;
    fontSize?: ReportFontSize | null;
  };
  content?: ReportRichTextInline[];
};

export type ReportRichTextDocument = {
  type: "doc";
  content?: ReportRichTextParagraph[];
};

export const emptyReportRichTextDocument: ReportRichTextDocument = { type: "doc", content: [] };

export function reportRichTextFromPlainText(value: string): ReportRichTextDocument {
  const content = value.split("\n").map((line): ReportRichTextParagraph => ({
    type: "paragraph",
    attrs: { textAlign: "left", fontSize: "normal" },
    content: line ? [{ type: "text", text: line }] : [],
  }));
  return { type: "doc", content: value ? content : [] };
}

export function reportRichTextPlainText(document: ReportRichTextDocument): string {
  return (document.content ?? []).map((paragraph) => (paragraph.content ?? []).map((inline) => inline.type === "hardBreak" ? "\n" : inline.text ?? "").join("")).join("\n");
}

export function reportRichTextCharacterCount(document: ReportRichTextDocument): number {
  return Array.from(reportRichTextPlainText(document)).length;
}

function reportNarrativeFitsGrid(document: ReportRichTextDocument, gridSize: 1 | 2 | 4): boolean {
  if (gridSize === 1 || !reportRichTextPlainText(document).trim()) return false;
  const weightedLength = (document.content ?? []).reduce((total, paragraph, paragraphIndex) => {
    const fontFactor = paragraph.attrs?.fontSize === "large" ? 1.2 : paragraph.attrs?.fontSize === "small" ? 0.8 : 1;
    const contentWeight = (paragraph.content ?? []).reduce((paragraphTotal, inline) => {
      if (inline.type === "hardBreak") return paragraphTotal + 60;
      return paragraphTotal + Array.from(inline.text ?? "").reduce((textTotal, character) => {
        if (/\s/.test(character) || /[.,:;!'|ijl]/.test(character)) return textTotal + 0.5;
        if (/[MW@%]/.test(character)) return textTotal + 1.5;
        return textTotal + 1;
      }, 0);
    }, 0);
    return total + contentWeight * fontFactor + (paragraphIndex ? 30 : 0);
  }, 0);
  return weightedLength <= (gridSize === 2 ? 1500 : 700);
}

export function reportIntroductionFitsGrid(document: ReportRichTextDocument, gridSize: 1 | 2 | 4): boolean {
  return reportNarrativeFitsGrid(document, gridSize);
}

export function reportConclusionFitsGrid(document: ReportRichTextDocument, gridSize: 1 | 2 | 4): boolean {
  return reportNarrativeFitsGrid(document, gridSize);
}

export function reportProfileSenderDocument(profile: { sender_block?: string; sender_document?: ReportRichTextDocument }): ReportRichTextDocument {
  return profile.sender_document?.type === "doc" ? profile.sender_document : reportRichTextFromPlainText(profile.sender_block ?? "");
}

export function reportProfileFooterDocument(profile: { footer?: string; footer_document?: ReportRichTextDocument }): ReportRichTextDocument {
  return profile.footer_document?.type === "doc" ? profile.footer_document : reportRichTextFromPlainText(profile.footer ?? "");
}
